import {DatasetMultiSelect} from "components/basic/dropdowns/dataset/DatasetMultiSelect";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import {ICommonComponentEditorProps} from "components/ComponentType";
import {
    defaultDatasetTableConfig,
    totalTypeDropdownSelectorOptions
} from "components/dashboard/dataset-table/dataset-table-editor/defaultDatasetTableEditorConfigs";
import {GroupedHeadersDatasetSelector} from "components/dashboard/dataset-table/GroupedHeadersDatasetSelector";
import {DatasetTableMode} from "components/dashboard/dataset-table/utils/interfaces";
import {ComponentConfigDefault, DatasetFullDefaultConfig} from "models/dataset-models";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import {ProgressSpinner} from "primereact/progressspinner";
import React, {lazy, Suspense, useCallback, useEffect, useState} from "react";

const DatasetTableDatasetEditor = lazy(() => import("components/dashboard/dataset-table/dataset-table-editor/dataset-table-dataset-editor/DatasetTableDatasetEditor"));

const DatasetTableEditor: React.FC<ICommonComponentEditorProps> = ({config, onConfigChange}) => {
    let conf: DatasetFullDefaultConfig = config ?? defaultDatasetTableConfig;

    if (Object.keys(conf).length === 0) {
        conf = defaultDatasetTableConfig;
    }

    const [currentConfig, setCurrentConfig] = useState(conf);
    const [selectedDatasets, setSelectedDatasets] = useState<string[]>();
    const [datasetEditorIsVisible, setDatasetEditorVisibility] = useState(false);
    const isSingleMode = currentConfig.mode === DatasetTableMode.SINGLE || currentConfig.mode === undefined;

    if (conf.datasets && conf.datasets.length > 0 && !selectedDatasets) {
        const datasets = conf.datasets.map((dataset: any) => {
            return dataset.identifier;
        });
        setSelectedDatasets(datasets);
    }

    const setConfigPropertyState = useCallback((property: keyof ComponentConfigDefault, value: any) => {
        const newConfig = structuredClone(currentConfig);
        if (newConfig.config) {
            newConfig.config[property] = value;
        }

        setCurrentConfig(newConfig);
    }, [currentConfig]);

    const handleConfigChange = useCallback((id: string, value: any): void => {
        setConfigPropertyState(id as keyof ComponentConfigDefault, value);
    }, [setConfigPropertyState]);

    const handleModeChange = useCallback((e: any): void => {
            const tempConfig = structuredClone(currentConfig);
            tempConfig.mode = e.value ? DatasetTableMode.GROUPED : DatasetTableMode.SINGLE;
            onConfigChange(tempConfig);
            setCurrentConfig(tempConfig);
        }
        , [currentConfig, onConfigChange]);

    const handleTimeUnitsChange = useCallback((e: any): void => {
        const tempConfig = structuredClone(currentConfig);

        tempConfig.config![e.target.id as keyof ComponentConfigDefault] = e.target.value.toString().toUpperCase();
        onConfigChange(tempConfig);
        setCurrentConfig(tempConfig);
    }, [currentConfig, onConfigChange]);

    const handleDatasetChange = useCallback((e: string[]) => {
        setSelectedDatasets(e);

        const selectedDatasetsArray = e.map((identifier) => {
            const oldDataset = currentConfig.datasets?.find((oldDataset) => {
                    return oldDataset.identifier === identifier;
                }
            );
            if (oldDataset) {
                return oldDataset;
            } 
                return {
                    identifier,
                };
            
        });

        setCurrentConfig({
            ...currentConfig,
            datasets: selectedDatasetsArray,
        });
    }, [currentConfig]);

    useEffect(() => {
        onConfigChange(currentConfig);
    }, [currentConfig]);

    return (
        <div className="grid p-fluid">
            {isSingleMode ? <div className="flex gap-2 w-full pl-2 pr-2">
                    <DatasetMultiSelect id="datasets" value={selectedDatasets} onChange={handleDatasetChange}/>
                    <Button
                        icon="pi pi-pencil"
                        type="button"
                        className="p-button-secondary"
                        tooltip="Edit dataset config"
                        onClick={() => setDatasetEditorVisibility(true)}
                    />
                    <Dialog
                        onHide={() => setDatasetEditorVisibility(false)}
                        visible={datasetEditorIsVisible}
                        header="Edit dataset config"
                        style={{width: "min(100vw, 1200px)", height: "500px"}}
                    >
                        <Suspense fallback={<ProgressSpinner/>}>
                            <DatasetTableDatasetEditor
                                currentConfig={currentConfig}
                                setCurrentConfig={setCurrentConfig}
                                onConfigChange={onConfigChange}
                            />
                        </Suspense>
                    </Dialog>
                </div> :
                <div className="col-12 gap-2 flex flex-column justify-content-center align-items-center">
                    <GroupedHeadersDatasetSelector currentConfig={currentConfig} setCurrentConfig={setCurrentConfig} />
                </div>}
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="mode">Column Headers</label>
                <InputSwitch
                    id="mode"
                    tooltip={currentConfig.mode === DatasetTableMode.SINGLE ? "Single" : "Grouped"}
                    checked={currentConfig.mode === DatasetTableMode.GROUPED}
                    onChange={(e) => handleModeChange(e)}
                />
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="transposed">Transposed</label>
                <InputSwitch
                    id="transposed"
                    checked={currentConfig.config?.transposed}
                    onChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                />
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="hideDateColumn">Hide Date Column</label>
                <InputSwitch
                    id="hideDateColumn"
                    checked={currentConfig.config?.hideDateColumn}
                    onChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                />
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="showEmpty">Show Empty</label>
                <InputSwitch
                    id="showEmpty"
                    checked={currentConfig.config?.showEmpty}
                    onChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                />
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="showTotal">Show Total</label>
                <InputSwitch
                    id="showTotal"
                    checked={currentConfig.config?.showTotal}
                    onChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                />
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="totalType">Total Type</label>
                <Dropdown
                    id="totalType"
                    value={currentConfig.config?.totalType}
                    options={totalTypeDropdownSelectorOptions}
                    onChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                />
            </div>
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="startOffset">Start Offset</label>
                <div className="p-inputgroup">
                    <InputNumber
                        id="startOffset"
                        value={currentConfig.config?.startOffset as number}
                        onValueChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                    />
                    <TimeUnitDropdown
                        id="startOffsetUnits"
                        className="p-inputgroup-addon p-0"
                        value={currentConfig.config?.startOffsetUnits ? currentConfig.config.startOffsetUnits : ""}
                        onChange={handleTimeUnitsChange}
                    />
                </div>
            </div>
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="stepSize">Step Size</label>
                <div className="p-inputgroup">
                    <InputNumber
                        id="stepSize"
                        value={currentConfig.config?.stepSize as number}
                        onValueChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                    />
                    <TimeUnitDropdown
                        id="stepUnits"
                        className="p-inputgroup-addon p-0"
                        value={currentConfig.config?.stepUnits ? currentConfig.config.stepUnits : ""}
                        onChange={handleTimeUnitsChange}
                    />
                </div>
            </div>
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="lagOffset">Lag Offset</label>
                <div className="p-inputgroup">
                    <InputNumber
                        id="lagOffset"
                        value={currentConfig.config?.lagOffset as number}
                        onValueChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                    />
                    <TimeUnitDropdown
                        id="lagUnits"
                        className="p-inputgroup-addon p-0"
                        value={currentConfig.config?.lagUnits ? currentConfig.config.lagUnits : ""}
                        onChange={handleTimeUnitsChange}
                    />
                </div>
            </div>
            <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="leadOffset">Lead Offset</label>
                <div className="p-inputgroup">
                    <InputNumber
                        id="leadOffset"
                        value={currentConfig.config?.leadOffset as number}
                        onValueChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                    />
                    <TimeUnitDropdown
                        id="leadUnits"
                        className="p-inputgroup-addon p-0"
                        value={currentConfig.config?.leadUnits ? currentConfig.config.leadUnits : ""}
                        onChange={handleTimeUnitsChange}
                    />
                </div>
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="alignStartTo">Align Start To</label>
                <TimeUnitDropdown
                    id="alignStartTo"
                    className="p-0"
                    value={currentConfig.config?.alignStartTo ? currentConfig.config.alignStartTo : ""}
                    onChange={handleTimeUnitsChange}
                />
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="startAutoRefreshFrequency">Update Frequency</label>
                <div className="p-inputgroup w-full">
                    <InputNumber
                        id="startAutoRefreshFrequency"
                        value={currentConfig.config?.startAutoRefreshFrequency as number}
                        onValueChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                    />
                    <span className="p-inputgroup-addon">s</span>
                </div>
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="dateFormat">Date Format</label>
                <TimeUnitDropdown
                    id="dateFormat"
                    className="p-0"
                    value={currentConfig.config?.dateFormat ? currentConfig.config.dateFormat : ""}
                    onChange={handleTimeUnitsChange}
                />
            </div>
            <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
                <label htmlFor="itemsPerPage">Items Per Page</label>
                <InputNumber
                    id="itemsPerPage"
                    value={currentConfig.config?.itemsPerPage}
                    onValueChange={(e) => handleConfigChange(e.target.id, e.target.value)}
                />
            </div>
        </div>
    );
};

export default DatasetTableEditor;